import React, { useState, useEffect } from 'react';
import * as dates from '@simplybenefits/sb-dates';
import Modal from './Modal.js';
import TextInput from '../TextFields/TextInput.js';
import PasswordConfirmation from './PasswordConfirmation.js';
import Text from '../Text.js';
import i18n from '../../localization/i18n.js';
import tokens from '../../localization/tokens.js';
import * as localizationMaps from '../../localization/maps.js';
import { optionsWithSpouse } from '../../services/selectFieldOptions.js';
import { initialSignageState, initialSignageErrorState, initialErrorState } from './common/stateInitialization.js';
import { validateInput } from '../../services/index.js';
import validateSignage from './validations/validateSignage.js';

/**
 * Initialize trustee data based on editing mode
 *
 * @param {Object} trusteeData - The trustee data object
 * @param {boolean} isEdit - Flag indicating if the data is for editing
 */
const setTrusteeData = (trusteeData, isEdit, isRemoveTrustee) => ({
  trusteeID: (isEdit || isRemoveTrustee) && trusteeData ? trusteeData.trusteeID : '',
  firstname: (isEdit || isRemoveTrustee) && trusteeData ? trusteeData.firstname : '',
  lastname: (isEdit || isRemoveTrustee) && trusteeData ? trusteeData.lastname : '',
  relationship: (isEdit || isRemoveTrustee) && trusteeData ? localizationMaps.fromEnglish("relationships", trusteeData.relationship) : localizationMaps.fromEnglish("relationships", "Child")
});

/**
 * Trustee Modal Component
 *
 * @param {Object} props - Component properties
 * @returns {JSX.Element} - Rendered component
 */
const TrusteeModal = ({
  open,
  loader,
  isEdit,
  isRemoveTrustee,
  onClose,
  addTrustee,
  editTrustee,
  removeTrustee,
  trusteeData,
  isAdmin,
  openChangeRequestModal
}) => {
  const [passwordConfirmationModalOpen, setPasswordConfirmationModalOpen] = useState(false);
  const [trustee, setTrustee] = useState(setTrusteeData(trusteeData, isEdit));
  const [errorState, setErrorState] = useState({
    firstnameError: false,
    lastnameError: false,
    relationshipError: false
  });
  const [signage, setSignage] = useState(initialSignageState);
  const [signageErrorState, setSignageErrorState] = useState(initialSignageErrorState);
  useEffect(() => {
    setTrustee(setTrusteeData(trusteeData, isEdit, isRemoveTrustee));
    if (isRemoveTrustee && !isAdmin) {
      setPasswordConfirmationModalOpen(true);
    }
  }, [trusteeData, isEdit, isRemoveTrustee]);
  const {
    firstnameError,
    lastnameError,
    relationshipError
  } = errorState;
  const {
    trusteeID,
    firstname,
    lastname,
    relationship
  } = trustee;

  /**
   * Closes the modal and resets form states.
   */
  const close = () => {
    setTrustee(setTrusteeData(trusteeData));
    setErrorState(initialErrorState);
    setPasswordConfirmationModalOpen(false);
    setSignageErrorState(initialSignageErrorState);
    setSignage(initialSignageState);
    onClose();
  };

  /**
   * Handles back button action from password confirmation modal
   */
  const onBack = () => setPasswordConfirmationModalOpen(false);

  /**
   * 
   * @param {*} field 
   * @param {*} value 
   */
  const handleSignageChange = (field, value) => {
    setSignage(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  /**
   * 
   * @param {*} field 
   * @param {*} value 
   */
  const handleInputChange = (field, value) => {
    setTrustee(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  /**
   * Handles getting changed data for changeRequest
   * @param {*} currentData 
   * @param {*} originalData 
   * @returns 
   */
  const getChangedTrusteeData = (currentData, originalData) => {
    const changedData = {};
    currentData.relationship = localizationMaps.toEnglish("relationships", currentData.relationship);
    if (currentData.firstname !== originalData.firstname) {
      changedData.firstname = currentData.firstname;
    }
    if (currentData.lastname !== originalData.lastname) {
      changedData.lastname = currentData.lastname;
    }
    if (currentData.relationship !== originalData.relationship) {
      changedData.relationship = currentData.relationship;
    }
    return changedData;
  };

  /**
   * Handles the save trustee action
   *
   * @returns {Promise<boolean>} - Save result
   */
  const save = async () => {
    if (isAdmin) {
      const changeRequestDetails = isEdit ? getChangedTrusteeData(trustee, trusteeData) : {
        firstname: trustee.firstname,
        lastname: trustee.lastname,
        relationship: localizationMaps.toEnglish("relationships", trustee.relationship)
      };
      let changeRequestData = {};
      if (isEdit) {
        if (Object.keys(changeRequestDetails).length === 0) {
          return false;
        }
        changeRequestData = {
          type: 'Edit Trustee',
          details: {
            trusteeID: trusteeData.trusteeID,
            ...changeRequestDetails
          }
        };
      } else {
        changeRequestData = {
          type: 'Add Trustee',
          details: {
            ...changeRequestDetails
          }
        };
      }
      const success = await openChangeRequestModal(changeRequestData);
      if (success) {
        close();
        return true;
      }
      return false;
    }
    const formErrors = await validateSignage({
      ...signage
    });
    if (formErrors !== false) {
      setSignageErrorState({
        ...formErrors
      });
      return false;
    }
    let success = false;
    if (isEdit) {
      success = await editTrustee(trusteeID, {
        firstname,
        lastname,
        relationship: localizationMaps.toEnglish("relationships", relationship),
        password: signage.password,
        signage: {
          name: signage.name,
          signature: signage.signature,
          timestampOfAcceptance: dates.getUTC(),
          acceptedTerms: signage.acceptedTerms,
          datedOn: dates.formatOutgoingDate(signage.datedOn)
        }
      });
    } else {
      success = await addTrustee({
        firstname,
        lastname,
        relationship: localizationMaps.toEnglish("relationships", relationship),
        password: signage.password,
        signage: {
          name: signage.name,
          signature: signage.signature,
          timestampOfAcceptance: dates.getUTC(),
          acceptedTerms: signage.acceptedTerms,
          datedOn: dates.formatOutgoingDate(signage.datedOn)
        }
      });
    }
    if (success) {
      close();
      return true;
    }
    return false;
  };

  /**
   * Handles the remove trustee action
   *
   * @returns {Promise<boolean|null>} - Remove result
   */
  const remove = async () => {
    if (isAdmin) {
      const changeRequestData = {
        type: 'Remove Trustee',
        details: {
          trusteeID
        }
      };
      const success = await openChangeRequestModal(changeRequestData);
      if (success) {
        close();
        return true;
      }
      return false;
    }
    const formErrors = await validateSignage({
      ...signage
    });
    if (formErrors !== false) {
      setSignageErrorState({
        ...formErrors
      });
      return false;
    }
    const success = await removeTrustee(trusteeID, signage.password, {
      name: signage.name,
      signature: signage.signature,
      timestampOfAcceptance: dates.getUTC(),
      acceptedTerms: signage.acceptedTerms,
      datedOn: dates.formatOutgoingDate(signage.datedOn)
    });
    if (success) {
      setTrustee(setTrusteeData(trustee));
      setErrorState(initialErrorState);
      setPasswordConfirmationModalOpen(false);
      setSignageErrorState(initialSignageErrorState);
      setSignage(initialSignageState);
      onClose();
    }
    return null;
  };

  /**
   * Validates form data for trustee
   *
   * @returns {Promise<boolean>} - Validation result
   */
  const validateFormData = async () => {
    const errors = {}; // Object to store validation errors

    errors.firstnameError = await validateInput('firstname', firstname);
    errors.lastnameError = await validateInput('lastname', lastname);
    errors.genderError = await validateInput('relationship', relationship);

    // Check for any errors
    if (Object.values(errors).some(Boolean)) {
      setErrorState({
        ...errors
      });
      return false;
    }
    if (isAdmin) {
      return save();
    }
    return setPasswordConfirmationModalOpen(true);
  };

  /**
   * 
   * @returns 
   */
  const setTitle = () => {
    if (passwordConfirmationModalOpen) {
      return i18n.t(tokens.titles["confirm changes"]);
    }
    if (isAdmin && isRemoveTrustee) {
      return i18n.t(tokens.titles["remove trustee"]);
    }
    if (isEdit) {
      return i18n.t(tokens.titles["edit trustee"]);
    }
    return i18n.t(tokens.titles["add trustee"]);
  };

  /**
   * 
   * @returns 
   */
  const setModalOnClick = () => {
    if (isAdmin && isRemoveTrustee) {
      return remove;
    }
    if (passwordConfirmationModalOpen) {
      return isRemoveTrustee ? remove : save;
    }
    return validateFormData;
  };
  return /*#__PURE__*/React.createElement(Modal, {
    testid: "add-edit-trustee-modal",
    open: open,
    onClose: close,
    onBack: passwordConfirmationModalOpen && !isRemoveTrustee ? onBack : null,
    title: setTitle(),
    primaryButton: {
      label: passwordConfirmationModalOpen ? i18n.t(tokens.buttons.finish) : i18n.t(tokens.buttons["save trustee"]),
      onClick: setModalOnClick()
    },
    loader: loader
  }, passwordConfirmationModalOpen ? /*#__PURE__*/React.createElement(PasswordConfirmation, {
    handleInputChange: handleSignageChange,
    signage: signage,
    signageErrorState: signageErrorState,
    title: i18n.t(tokens.labels.account.confirmTrusteeChanges)
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, isAdmin && isRemoveTrustee ? /*#__PURE__*/React.createElement(Text, null, i18n.t(tokens.labels.changeRequests.areYouSureRemoveTrustee, {
    trusteeName: `${trustee.firstname} ${trustee.lastname}`
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.firstname),
    value: firstname,
    onChange: e => handleInputChange('firstname', e.target.value),
    error: Boolean(firstnameError),
    helperText: firstnameError,
    fullWidth: true,
    autoFocus: true
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.lastname),
    value: lastname,
    onChange: e => handleInputChange('lastname', e.target.value),
    error: Boolean(lastnameError),
    helperText: lastnameError,
    fullWidth: true
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.relationship),
    value: relationship,
    fullWidth: true,
    onChange: e => handleInputChange('relationship', e.target.value),
    error: Boolean(relationshipError),
    helperText: relationshipError,
    select: true,
    selectOptions: optionsWithSpouse()
  }))));
};
export default TrusteeModal;